import { z } from 'zod';

/**
 * Drone-marketplace money / record-keeping vocabulary — Drone D1 #317.
 *
 * Same anti-clobber FK-lookup discipline as the capability taxonomy (./drone-taxonomy)
 * and the operator-action verbs (Task #90 / migration 0045): each list is THIS
 * const-tuple; the DB enforces it via an FK into a `valid_*` lookup table (migration
 * 0089), NOT a CHECK enum. Pinned by `drone-marketplace.test.ts`.
 *
 * SCOPE (#317): VOCABULARY + the billable CONVENTION only. The mission ledger RECORDS
 * usage; there is NO charging logic, NO state machine (#319), NO matching (#320), and
 * the fee / take-rate levers default to 0. Money model: docs/drone-feature-plan +
 * docs/drone-marketplace-cto-brief §A/§B.
 */

/**
 * Operator kind — who flies the asset. Drives the `billable` convention: government
 * fleets and Ontario Corps volunteer assets are tasked + tracked + feed-patched in the
 * SAME mission ledger but sit OUT of the money rails (no service fee). Private
 * operators are the billable marketplace supply. (brief §A "Government & Ontario Corps
 * assets = no charge"; §B `billable`; §C "keep billable=false paths … out of the money
 * rails but in the same mission ledger for tracking/audit".)
 *   - private        — a vetted commercial operator → billable.
 *   - government     — a government-owned fleet      → NOT billable.
 *   - ontario_corps  — an Ontario Corps volunteer asset → NOT billable.
 */
export const OPERATOR_KINDS = [
  'private',
  'government',
  'ontario_corps',
] as const;

export const OperatorKindSchema = z.enum(OPERATOR_KINDS);
export type OperatorKind = z.infer<typeof OperatorKindSchema>;

/**
 * The billable-by-default convention per operator kind. Encoded in DATA — the
 * `valid_operator_kinds.default_billable` column (migration 0089) carries the same
 * mapping, pinned to this constant by the lockstep test — so #319's ledger logic reads
 * the default from the lookup row instead of hard-coding it. Only `private` is billable
 * in v1; gov + Ontario Corps are out of the money rails.
 */
export const DEFAULT_BILLABLE_BY_KIND: Readonly<Record<OperatorKind, boolean>> = {
  private: true,
  government: false,
  ontario_corps: false,
} as const;

/** The default-billable flag for an operator kind (the gov/OC = false convention). */
export function operatorKindIsBillable(kind: OperatorKind): boolean {
  return DEFAULT_BILLABLE_BY_KIND[kind];
}

/**
 * Managed-ISR subscription tier — the recurring ARR product (brief §A "Managed-ISR
 * software module is a separate subscription … an annual module fee", priced
 * CA$25k→75k). The docs name only the price band, not tier labels; these three tiers
 * span that band and are refinable later with one idempotent INSERT (the whole point
 * of the FK-lookup pattern). `org_subscriptions.tier` FK-references this vocabulary.
 */
export const SUBSCRIPTION_TIERS = [
  'standard',
  'professional',
  'enterprise',
] as const;

export const SubscriptionTierSchema = z.enum(SUBSCRIPTION_TIERS);
export type SubscriptionTier = z.infer<typeof SubscriptionTierSchema>;

/** Coerce an arbitrary value into an `OperatorKind`, `null` on unexpected input. */
export function parseOperatorKind(value: unknown): OperatorKind | null {
  const parsed = OperatorKindSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

/** Coerce an arbitrary value into a `SubscriptionTier`, `null` on unexpected input. */
export function parseSubscriptionTier(value: unknown): SubscriptionTier | null {
  const parsed = SubscriptionTierSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}
